import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Shield } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-900">
      {/* Language Switcher */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher />
      </div>

      {/* Back Button */}
      <div className="absolute top-4 left-4 z-20">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => navigate(-1)}
          className="bg-slate-800/50 border-slate-600 text-slate-300 hover:bg-slate-700/50"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('back')}
        </Button>
      </div>

      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900">
        <div className="absolute inset-0 opacity-40" style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"
        }}></div>
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-4xl bg-slate-800/80 backdrop-blur-lg border-slate-700 shadow-2xl">
          <CardHeader className="text-center space-y-2">
            <CardTitle className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent flex items-center justify-center gap-2">
              <Shield className="w-8 h-8 text-purple-400" />
              {t('privacyPolicy')}
            </CardTitle>
            <CardDescription className="text-slate-300">
              {t('lastUpdated')}: 2024年12月19日
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6 text-slate-300 max-h-96 overflow-y-auto">
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">1. {t('informationCollection')}</h2>
              <p className="text-sm leading-relaxed mb-2">
                {t('informationCollectionText')}
              </p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>{t('personalInfo')}</li>
                <li>{t('businessInfo')}</li>
                <li>{t('usageData')}</li>
                <li>{t('technicalData')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">2. {t('howWeUseInfo')}</h2>
              <p className="text-sm leading-relaxed mb-2">
                {t('howWeUseInfoText')}
              </p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>{t('provideServices')}</li>
                <li>{t('improveServices')}</li>
                <li>{t('customerSupport')}</li>
                <li>{t('securityMaintenance')}</li>
                <li>{t('legalCompliance')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">3. {t('informationSharing')}</h2>
              <p className="text-sm leading-relaxed mb-2">
                {t('informationSharingText')}
              </p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>{t('serviceProviders')}</li>
                <li>{t('legalRequirements')}</li>
                <li>{t('businessTransfers')}</li>
                <li>{t('consentBased')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">4. {t('dataSecurity')}</h2>
              <p className="text-sm leading-relaxed mb-2">
                {t('dataSecurityText')}
              </p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>{t('encryption')}</li>
                <li>{t('accessControls')}</li>
                <li>{t('regularAudits')}</li>
                <li>{t('secureDataCenters')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">5. {t('dataRetention')}</h2>
              <p className="text-sm leading-relaxed">
                {t('dataRetentionText')}
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">6. {t('yourRights')}</h2>
              <p className="text-sm leading-relaxed mb-2">
                {t('yourRightsText')}
              </p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>{t('accessRight')}</li>
                <li>{t('correctionRight')}</li>
                <li>{t('deletionRight')}</li>
                <li>{t('portabilityRight')}</li>
                <li>{t('objectionRight')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">7. {t('cookiesAndTracking')}</h2>
              <p className="text-sm leading-relaxed">
                {t('cookiesTrackingText')}
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">8. {t('internationalTransfers')}</h2>
              <p className="text-sm leading-relaxed">
                {t('internationalTransfersText')}
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">9. {t('childrenPrivacy')}</h2>
              <p className="text-sm leading-relaxed">
                {t('childrenPrivacyText')}
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">10. {t('policyChanges')}</h2>
              <p className="text-sm leading-relaxed">
                {t('policyChangesText')}
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">11. {t('contactUs')}</h2>
              <p className="text-sm leading-relaxed">
                {t('contactUsText')}
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
